import styled from "@emotion/styled";
import type { MinimalTokenInfo } from "@stepfinance/common";
import { lightGreen, transparentWhite } from "@stepfinance/react";
import { Button, Collapse } from "antd";
import type { BaseButtonProps } from "antd/lib/button/button";
import type { FC } from "react";
import { useState } from "react";
import { CountdownCircleTimer } from "react-countdown-circle-timer";
import { usePageVisibility } from "react-page-visibility";

import { useLanguageContext } from "../../contexts/language/language";
import type { JupiterRoute } from "../../utils/jupiter/api";
import { EfficiencyInfo } from "./EfficiencyInfo";
import { FeesInfo } from "./FeesInfo";
import { StyledChevron } from "./RouteSelector";

const { Panel } = Collapse;

export type SwapTradeInfoProps = {
  inTokenInfo?: MinimalTokenInfo;
  outTokenInfo?: MinimalTokenInfo;
  outPerInRate: number;
  route: JupiterRoute;
  slippageTolerance: number;
  loading: boolean;
  refreshRoute: () => void;
  stakedAccess: boolean;
  marketRate: number | null;
};

const REFRESH_TIMEOUT_SECS = 20;

export const SwapTradeInfo: FC<SwapTradeInfoProps> = ({
  loading,
  refreshRoute,
  route,
  slippageTolerance,
  stakedAccess,
  outPerInRate,
  inTokenInfo,
  outTokenInfo,
  marketRate,
}) => {
  const isVisible = usePageVisibility();
  const [timerKey, setTimerKey] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const {
    currentLanguageData: {
      pages: { swapLP: swapText },
    },
  } = useLanguageContext();

  function refreshTradeRoute() {
    setTimerKey((prevKey) => prevKey + 1);
    refreshRoute();
  }

  return (
    <>
      <StyledTradeCollapse
        expandIcon={() => <StyledChevron expanded={expanded} />}
        defaultActiveKey={1}
        onChange={() => setExpanded(!expanded)}
      >
        <StyledPanel
          header={
            <SwapDetailsContainer>
              <span className="label">{swapText.swapDetails}</span>
              <StyledRefreshButton
                type="default"
                onClick={(e) => {
                  e.stopPropagation();
                  refreshTradeRoute();
                }}
                disabled={loading}
              >
                <CountdownCircleTimer
                  key={timerKey}
                  isPlaying={isVisible && !loading}
                  duration={REFRESH_TIMEOUT_SECS}
                  onComplete={() => {
                    return {
                      delay: timerKey,
                      shouldRepeat: true,
                    };
                  }}
                  colors={loading ? "#aaa" : lightGreen}
                  size={16}
                  strokeWidth={2}
                />
              </StyledRefreshButton>
            </SwapDetailsContainer>
          }
          key={1}
        >
          <EfficiencyInfo
            inToken={inTokenInfo}
            outToken={outTokenInfo}
            outPerInRate={outPerInRate}
            route={route}
            slippageTolerance={slippageTolerance}
            marketRate={marketRate}
          />
          <FeesInfo route={route} stakedAccess={stakedAccess} />
        </StyledPanel>
      </StyledTradeCollapse>
    </>
  );
};

const StyledPanel = styled(Panel)`
  .ant-collapse-header {
    margin-bottom: 10px;
  }
`;

export const StyledTradeCollapse = styled(Collapse)`
  border: none;
  background: none;
  width: 100%;

  .ant-collapse-item {
    border: none;
  }

  .ant-collapse-header {
    padding: 0 !important;
    display: flex;
    align-items: center !important;

    div {
      display: flex;
      align-items: center;
    }

    .ant-collapse-arrow {
      margin-right: 6px !important;
    }
  }

  .ant-collapse-content {
    border: none;
    border-radius: 0 0 0 0;
    background: none;
    overflow: hidden;
  }
`;

const SwapDetailsContainer = styled.div`
  display: flex;
  align-items: center;

  .label {
    font-size: 12px;
    color: ${transparentWhite};
    font-weight: 700;
  }
`;

const StyledRefreshButton = styled(Button)<BaseButtonProps>`
  display: flex;
  align-items: center;
  padding: 0;
  margin-left: 5px;
  border: none;
  height: 100%;

  svg,
  [aria-label="Countdown timer"] {
    margin: 0 auto;
    pointer-events: none;
  }
`;
